import { computed, getCurrentInstance } from 'vue';
import { useStore } from '@/utils/vue-api';
import { useNavigation } from '@rebuild/hooks/useNavigation';
import { useMenuTree } from '@rebuild/hooks/useMenuTree';

/**
 * 递归查找第一个模块
 * 从当前菜单开始向下递归查找，返回最靠前的 moduleVOS[0]
 *
 * @param {Object} menu - 菜单对象
 * @returns {Object|null} 找到的第一个模块，未找到返回 null
 */
function findFirstModule(menu) {
	if (!menu) {
		return null;
	}

	// 如果当前菜单有模块，返回第一个模块
	if (menu.moduleVOS && Array.isArray(menu.moduleVOS) && menu.moduleVOS.length > 0) {
		return menu.moduleVOS[0];
	}

	// 如果当前菜单没有模块，递归查找子菜单
	if (menu.pageMenuVOS && Array.isArray(menu.pageMenuVOS)) {
		for (const subMenu of menu.pageMenuVOS) {
			const found = findFirstModule(subMenu);
			if (found) {
				return found;
			}
		}
	}

	return null;
}

export const INJECT_KEY_OPENING_COMPLETE = 'openingComplete';

/**
 * 页面显示逻辑 Hook
 * 基于路由参数自动计算显示类型，无需手动初始化
 *
 * @returns {Object} 显示逻辑相关的计算属性
 */
export function useDisplayLogic() {
	const { proxy } = getCurrentInstance();
	const store = useStore();

	const route = computed(() => proxy.$route);
	const menuId = computed(() => route.value.params.menuId);
	const moduleId = computed(() => route.value.params.moduleId);

	const getPageData = computed(() => store.getters.getCommonData || {});
	const getRenderMenus = computed(() => {
		const menus = getPageData.value?.pageMenuVOS || [];
		const excludeDefaultMenus = (menus || []).filter(v => !v.isDefault);
		return excludeDefaultMenus;
	});

	// 当前菜单信息
	const displayMenu = computed(() => {
		const sourceMenuId = menuId.value;
		const menu = getMenuByUuid(sourceMenuId);
		return menu?.meta || {};
	});

	// 当前模块信息
	const displayModule = computed(() => {
		const menu = displayMenu.value;
		if (!menu) return null;

		const sourceModuleId = moduleId.value;
		if (sourceModuleId) {
			return menu.moduleVOS?.find(module => module.moduleId === sourceModuleId) || null;
		}

		// 如果没有指定模块ID，向下递归查找第一个模块
		return findFirstModule(menu);
	});

	// 模块列表
	const displayModuleList = computed(() => {
		const menu = displayMenu.value;
		return menu?.moduleVOS || [];
	});

	// 菜单展示类型
	const displayType = computed(() => {
		const menu = displayMenu.value;
		if (!menu) return null;

		const sourceModuleId = moduleId.value;

		// 如果指定了模块ID，显示详情
		if (sourceModuleId) {
			return displayModule.value ? 'detail' : null;
		}

		// displayMode 0=简览 1=详情
		return menu?.displayMode === 0 ? 'overview' : 'detail';
	});

	const isOverview = computed(() => displayType.value === 'overview');
	const isDetail = computed(() => displayType.value === 'detail');

	const { viewPageInSurf } = useNavigation();
	const handleSurfNavigate = ({ menu, module }) => {
		// 展示类型 1:跳转链接 2:模块
		if (menu.showType === 1) {
			return window.open(menu.menuUrl);
		}

		viewPageInSurf({
			menu,
			module
		});
	};

	const { getMenuByUuid } = useMenuTree();
	const checkIsLinkMenu = uuid => {
		const menu = getMenuByUuid(uuid);
		const { showType } = menu?.meta || {};
		return showType === 1;
	};

	const isBannerFirst = computed(() => {
		const modules = displayModuleList.value;
		if (modules.length === 0) return false;
		return modules[0]?.moduleId === 'new_banner';
	});

	return {
		// 数据
		getRenderMenus,
		displayMenu,
		displayModule,
		displayModuleList,
		menuId,
		moduleId,

		// 状态
		displayType,
		isOverview,
		isDetail,

		// 导航
		handleSurfNavigate,

		checkIsLinkMenu,
		isBannerFirst
	};
}
